import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { API } from '../constants/api';

/**
 * ShareLinkService — Axios client + API methods for public, JWE-tokenized share-link pages.
 *
 * Distinct from `axiosInterceptor` because:
 *   - It MUST NOT auto-attach the user's login JWT (`localStorage.token`). A
 *     viewer of a share link is anonymous; sending their login JWT — if they
 *     happen to be logged in to another account in the same browser — would
 *     send the wrong credentials and could mask token-based auth failures.
 *   - On 401 it does NOT redirect to /login or clear localStorage. The user
 *     may be perfectly logged in elsewhere; an expired share token is just an
 *     "Invalid or expired link" state to render in the share-link page itself.
 *
 * The active share token is set ONCE per page-load via setShareToken(token),
 * read out of `?t=...` in the share-link page component on mount.
 */

const BaseApiUrl = (window as unknown as { API_URL: string }).API_URL;

let activeShareToken: string | null = null;

export const setShareToken = (token: string | null): void => {
    activeShareToken = token;
};

export const getShareToken = (): string | null => activeShareToken;

export const publicAxios = axios.create({
    baseURL: BaseApiUrl,
    headers: {
        // Skips ngrok's browser-warning interstitial when the backend is tunneled via ngrok for local dev
        'ngrok-skip-browser-warning': 'true',
    },
});

publicAxios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        if (activeShareToken && config.headers) {
            config.headers.Authorization = `Bearer ${activeShareToken}`;
        }

        return config;
    },
    (error: AxiosError) => Promise.reject(error)
);

// Unwrap response.data when present (matches axiosInterceptor behavior so the
// shape callers see is identical between auth and public flows).
publicAxios.interceptors.response.use(
    (response) => (response.status === 200 ? response.data : response),
    (error: AxiosError) => Promise.reject(error)
);

// ───── Error status codes ───────────────────────────────────────────────────

export const ERROR_STATUS = {
    EXPIRED: 401,
    NOT_FOUND: 404,
} as const;

export const LOAD_STATE = {
    LOADING: 'loading',
    SUCCESS: 'success',
    INVALID_TOKEN: 'invalid-token',
    EXPIRED: 'expired',
    NOT_FOUND: 'not-found',
    // Order exists and the token is valid, but the shipment was cancelled — the public page
    // shows an in-page notice instead of tracking data (never a redirect into the private app).
    CANCELLED: 'cancelled',
    ERROR: 'error',
} as const;

export type LoadState = (typeof LOAD_STATE)[keyof typeof LOAD_STATE];

// ───── API methods ─────────────────────────────────────────────────────────

export const getSharedQuote = async () => publicAxios.get(API.SHARE_LINK.GET_QUOTE);

export const getSharedActiveQuote = async () => publicAxios.get(API.SHARE_LINK.GET_ACTIVE_QUOTE);

export const getSharedQuoteActivities = async (params: { page?: number; perPage?: number; lastId?: number }) => publicAxios.get(API.SHARE_LINK.GET_ACTIVITIES, { params });

export const getSharedQuoteMessages = async (params: { page?: number; perPage?: number; lastId?: number; entityType?: string }) => publicAxios.get(API.SHARE_LINK.GET_MESSAGES, { params });

export const getSharedOrder = async () => publicAxios.get(API.SHARE_LINK.GET_ORDER);

export const getSharedOrderActivities = async (params: { page?: number; perPage?: number; lastId?: number }) => publicAxios.get(API.SHARE_LINK.GET_ORDER_ACTIVITIES, { params });

export const getSharedRadarOrder = async () => publicAxios.get(API.SHARE_LINK.GET_RADAR_ORDER);

/** One document the customer shares on this link, as a Blob (the share token is the auth). */
export const downloadSharedDocument = async (kind: 'order' | 'radar', documentId: number): Promise<Blob> =>
    publicAxios.get(`${kind === 'radar' ? API.SHARE_LINK.RADAR_ORDER_DOCUMENT : API.SHARE_LINK.ORDER_DOCUMENT}/${documentId}`, { responseType: 'blob' }) as unknown as Promise<Blob>;

export const getSharedRadarOrderActivities = async (params: { page?: number; perPage?: number; lastId?: number }) => publicAxios.get(API.SHARE_LINK.GET_RADAR_ORDER_ACTIVITIES, { params });

export const getSharedRadarOrderMessages = async (params: { page?: number; perPage?: number; lastId?: number }) => publicAxios.get(API.SHARE_LINK.GET_RADAR_ORDER_MESSAGES, { params });
